import type { E2EAgentUsageRun } from "../api/admin";

export type UsageGranularity = "daily" | "weekly" | "monthly";

export interface UsageBucket {
  /** Stable bucket identity (local-time period start, ISO date). */
  key: string;
  /** Local-time period start. */
  start: Date;
  label: string;
  runs: number;
  succeeded: number;
  failed: number;
  expired: number;
  canceled: number;
  inFlight: number;
  /** succeeded / (succeeded + failed + expired); null without terminal runs. */
  successRate: number | null;
  medianDurationSeconds: number | null;
  p90DurationSeconds: number | null;
  /** Sum of retained list-price estimates for runs created in the bucket. */
  estimatedCostUsd: number;
  /** Runs whose trace summary recorded a cost estimate. */
  costedRuns: number;
  /** Mean estimate across costed runs; null when none carry an estimate. */
  avgCostPerRunUsd: number | null;
  /** Sum of retained subagent counts for runs with trace summaries. */
  subagents: number;
  /** Runs whose trace summary recorded a subagent count. */
  tracedRuns: number;
  /** Mean subagent count across runs with trace summaries. */
  avgSubagentsPerRun: number | null;
  /** Sum of retained total-token counts. */
  totalTokens: number;
  /** Runs whose trace summary recorded total tokens. */
  tokenCountedRuns: number;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Monday-start week, matching how run cohorts are discussed internally. */
function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  const offset = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - offset);
  return day;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function bucketStart(date: Date, granularity: UsageGranularity): Date {
  if (granularity === "weekly") return startOfWeek(date);
  if (granularity === "monthly") return startOfMonth(date);
  return startOfDay(date);
}

function nextBucketStart(start: Date, granularity: UsageGranularity): Date {
  const next = new Date(start);
  if (granularity === "daily") next.setDate(next.getDate() + 1);
  if (granularity === "weekly") next.setDate(next.getDate() + 7);
  if (granularity === "monthly") next.setMonth(next.getMonth() + 1);
  return next;
}

function bucketKey(start: Date): string {
  const month = String(start.getMonth() + 1).padStart(2, "0");
  const day = String(start.getDate()).padStart(2, "0");
  return `${start.getFullYear()}-${month}-${day}`;
}

export function bucketLabel(
  start: Date,
  granularity: UsageGranularity,
): string {
  if (granularity === "monthly") {
    return start.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
  }
  return start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function quantile(sorted: readonly number[], q: number): number | null {
  if (sorted.length === 0) return null;
  const position = (sorted.length - 1) * q;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const lowerValue = sorted[lower];
  const upperValue = sorted[upper];
  if (lowerValue === undefined || upperValue === undefined) return null;
  return lowerValue + (upperValue - lowerValue) * (position - lower);
}

export function costOf(run: E2EAgentUsageRun): number | null {
  const raw = run.estimated_cost_usd;
  if (raw === null || raw === undefined) return null;
  const value = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Fold usage runs into contiguous local-time buckets covering [since, until].
 * Buckets without runs are kept so time series show real gaps.
 */
export function aggregateUsage(
  runs: readonly E2EAgentUsageRun[],
  granularity: UsageGranularity,
  since: Date,
  until: Date,
): UsageBucket[] {
  const grouped = new Map<string, E2EAgentUsageRun[]>();
  for (const run of runs) {
    const created = new Date(run.created_at);
    if (Number.isNaN(created.getTime())) continue;
    const key = bucketKey(bucketStart(created, granularity));
    const existing = grouped.get(key);
    if (existing) existing.push(run);
    else grouped.set(key, [run]);
  }

  const buckets: UsageBucket[] = [];
  const firstStart = bucketStart(since, granularity);
  const lastStart = bucketStart(until, granularity);
  for (
    let start = firstStart;
    start.getTime() <= lastStart.getTime();
    start = nextBucketStart(start, granularity)
  ) {
    const bucketRuns = grouped.get(bucketKey(start)) ?? [];
    let succeeded = 0;
    let failed = 0;
    let expired = 0;
    let canceled = 0;
    let inFlight = 0;
    let estimatedCostUsd = 0;
    let costedRuns = 0;
    let subagents = 0;
    let tracedRuns = 0;
    let totalTokens = 0;
    let tokenCountedRuns = 0;
    const durations: number[] = [];
    for (const run of bucketRuns) {
      if (run.outcome === "succeeded") succeeded += 1;
      else if (run.outcome === "failed") failed += 1;
      else if (run.outcome === "expired") expired += 1;
      else if (run.outcome === "canceled") canceled += 1;
      else inFlight += 1;
      const terminal =
        run.outcome === "succeeded" ||
        run.outcome === "failed" ||
        run.outcome === "expired";
      if (
        terminal &&
        run.duration_seconds !== null &&
        run.duration_seconds !== undefined
      ) {
        durations.push(run.duration_seconds);
      }
      const cost = costOf(run);
      if (cost !== null) {
        estimatedCostUsd += cost;
        costedRuns += 1;
      }
      if (run.subagent_count !== null && run.subagent_count !== undefined) {
        subagents += run.subagent_count;
        tracedRuns += 1;
      }
      if (run.total_tokens !== null && run.total_tokens !== undefined) {
        totalTokens += run.total_tokens;
        tokenCountedRuns += 1;
      }
    }
    durations.sort((a, b) => a - b);
    const decided = succeeded + failed + expired;
    buckets.push({
      key: bucketKey(start),
      start,
      label: bucketLabel(start, granularity),
      runs: bucketRuns.length,
      succeeded,
      failed,
      expired,
      canceled,
      inFlight,
      successRate: decided > 0 ? succeeded / decided : null,
      medianDurationSeconds: quantile(durations, 0.5),
      p90DurationSeconds: quantile(durations, 0.9),
      estimatedCostUsd,
      costedRuns,
      avgCostPerRunUsd: costedRuns > 0 ? estimatedCostUsd / costedRuns : null,
      subagents,
      tracedRuns,
      avgSubagentsPerRun: tracedRuns > 0 ? subagents / tracedRuns : null,
      totalTokens,
      tokenCountedRuns,
    });
  }
  return buckets;
}

export interface UsageTotals {
  runs: number;
  decided: number;
  succeeded: number;
  successRate: number | null;
  medianDurationSeconds: number | null;
  estimatedCostUsd: number;
  costedRuns: number;
  avgCostPerRunUsd: number | null;
  subagents: number;
  tracedRuns: number;
  avgSubagentsPerRun: number | null;
  totalTokens: number;
  tokenCountedRuns: number;
}

export function summarizeUsage(runs: readonly E2EAgentUsageRun[]): UsageTotals {
  let succeeded = 0;
  let decided = 0;
  let estimatedCostUsd = 0;
  let costedRuns = 0;
  let subagents = 0;
  let tracedRuns = 0;
  let totalTokens = 0;
  let tokenCountedRuns = 0;
  const durations: number[] = [];
  for (const run of runs) {
    const terminal =
      run.outcome === "succeeded" ||
      run.outcome === "failed" ||
      run.outcome === "expired";
    if (terminal) {
      decided += 1;
      if (run.outcome === "succeeded") succeeded += 1;
      if (run.duration_seconds !== null && run.duration_seconds !== undefined) {
        durations.push(run.duration_seconds);
      }
    }
    const cost = costOf(run);
    if (cost !== null) {
      estimatedCostUsd += cost;
      costedRuns += 1;
    }
    if (run.subagent_count !== null && run.subagent_count !== undefined) {
      subagents += run.subagent_count;
      tracedRuns += 1;
    }
    if (run.total_tokens !== null && run.total_tokens !== undefined) {
      totalTokens += run.total_tokens;
      tokenCountedRuns += 1;
    }
  }
  durations.sort((a, b) => a - b);
  return {
    runs: runs.length,
    decided,
    succeeded,
    successRate: decided > 0 ? succeeded / decided : null,
    medianDurationSeconds: quantile(durations, 0.5),
    estimatedCostUsd,
    costedRuns,
    avgCostPerRunUsd: costedRuns > 0 ? estimatedCostUsd / costedRuns : null,
    subagents,
    tracedRuns,
    avgSubagentsPerRun: tracedRuns > 0 ? subagents / tracedRuns : null,
    totalTokens,
    tokenCountedRuns,
  };
}

export function formatSubagentsPerRun(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value < 0) return "—";
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function formatTokenCount(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value < 0) return "—";
  return Math.round(value).toLocaleString();
}

export function formatPercent(rate: number | null): string {
  if (rate === null) return "—";
  return `${(rate * 100).toFixed(rate >= 0.995 || rate === 0 ? 0 : 1)}%`;
}

export function formatDurationSeconds(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const rounded = Math.round(seconds);
  if (rounded >= 3600) {
    return `${Math.floor(rounded / 3600)}h ${Math.floor((rounded % 3600) / 60)}m`;
  }
  if (rounded >= 60) return `${Math.floor(rounded / 60)}m ${rounded % 60}s`;
  return `${rounded}s`;
}

export function formatUsd(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2,
  });
}
